import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarCheck, ChevronLeft, ChevronRight, Loader2, Unlink, AlertTriangle } from "lucide-react";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const toMin = (hhmm) => { const [h, m] = hhmm.split(":").map(Number); return h * 60 + m; };
const fmt = (t) => `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
const START = 8 * 60, END = 20 * 60, STEP = 30;

export default function ConsultantAgenda({ defaultDate }) {
  const [consultants, setConsultants] = useState([]);
  const [consultantId, setConsultantId] = useState("");
  const [date, setDate] = useState(defaultDate || iso(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/consultants").then(({ data }) => { setConsultants(data); if (data[0]) setConsultantId(data[0].id); }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!consultantId || !date) { setData(null); return; }
    setLoading(true);
    try { const { data } = await api.get("/calendar/freebusy", { params: { consultant_id: consultantId, date } }); setData(data); }
    catch (e) { setData(null); } finally { setLoading(false); }
  }, [consultantId, date]);
  useEffect(() => { load(); }, [load]);

  const busyMin = useMemo(() => (data?.busy || []).map((b) => [toMin(b.start), toMin(b.end)]), [data]);
  const slots = useMemo(() => { const s = []; for (let t = START; t < END; t += STEP) s.push(t); return s; }, []);
  const isBusy = (t) => busyMin.some(([s, e]) => t < e && t + STEP > s);
  const shift = (n) => { const d = new Date(date); d.setDate(d.getDate() + n); setDate(iso(d)); };
  const selName = consultants.find((c) => c.id === consultantId)?.name || "";

  return (
    <div className="ds-card space-y-3 p-3" data-testid="consultant-agenda">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-display font-semibold text-slate-800">
          <CalendarCheck className="h-5 w-5 text-brand" /> Agenda consulente (Google)
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={consultantId} onValueChange={setConsultantId}>
            <SelectTrigger className="h-9 w-[180px]" data-testid="agenda-consultant-select"><SelectValue placeholder="Consulente" /></SelectTrigger>
            <SelectContent className="bg-white">{consultants.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={() => shift(-1)} data-testid="agenda-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Input type="date" className="h-9 w-[150px]" value={date} onChange={(e) => setDate(e.target.value)} data-testid="agenda-date" />
          <Button size="sm" variant="outline" onClick={() => shift(1)} data-testid="agenda-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-brand" /></div>
      ) : !data ? (
        <div className="py-6 text-center text-sm text-slate-400" data-testid="agenda-status">Seleziona un consulente.</div>
      ) : !data.configured ? (
        <div className="flex items-center gap-2 rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-sm text-slate-600" data-testid="agenda-status">
          <AlertTriangle className="h-4 w-4 text-warning" /> Integrazione Google non ancora configurata.
        </div>
      ) : data.error ? (
        <div className="flex items-center gap-2 rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-slate-600" data-testid="agenda-status">
          <AlertTriangle className="h-4 w-4 text-danger" /> {data.detail || `Impossibile leggere la disponibilità di ${selName}.`}
        </div>
      ) : !data.connected ? (
        <div className="flex items-center gap-2 rounded-md border border-border bg-slate-50 px-3 py-2 text-sm text-slate-500" data-testid="agenda-status">
          <Unlink className="h-4 w-4" /> Calendario di {selName} non collegato. La Direzione può collegarlo da Impostazioni → Google Calendar.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-4 text-xs text-slate-500" data-testid="agenda-status">
            <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-success/70" /> Libero</span>
            <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-danger/70" /> Occupato</span>
            <span className="text-slate-400">Solo disponibilità · nessun dettaglio privato</span>
          </div>
          <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6 md:grid-cols-8" data-testid="agenda-grid">
            {slots.map((t) => {
              const b = isBusy(t);
              return (
                <div key={t} data-testid={`agenda-slot-${fmt(t)}`}
                  className={`rounded-md px-2 py-1.5 text-center text-xs font-medium ${b ? "bg-danger/15 text-danger" : "bg-success/10 text-success"}`}>
                  <div>{fmt(t)}</div>
                  <div className="text-[10px]">{b ? "Occupato" : "Libero"}</div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
